/**
 * IndexedDBStorage - Unified Modern Persistence Engine for Crazy Cribbage
 * Provides high-capacity, non-blocking IndexedDB persistence with an in-memory cache,
 * W3C Storage compatibility (getItem, setItem, removeItem, clear), Promise-based async API,
 * and automatic legacy localStorage migration.
 */

export class IndexedDBStorage {
  /**
   * @param {Object} [options={}]
   * @param {string} [options.dbName='crazy_cribbage_db']
   * @param {string} [options.storeName='app_storage']
   * @param {number} [options.version=1]
   * @param {boolean} [options.migrateLocalStorage=true]
   */
  constructor(options = {}) {
    this.dbName = options.dbName || 'crazy_cribbage_db';
    this.storeName = options.storeName || 'app_storage';
    this.version = options.version || 1;
    this.migrateLocalStorage = options.migrateLocalStorage !== false;

    /** @type {Map<string, string>} In-memory fast synchronous cache */
    this._cache = new Map();

    // Synchronously pre-populate cache from localStorage so immediate reads never fail
    this._prepopulateFromLocalStorage();

    /** @type {IDBDatabase|null} */
    this._db = null;
    /** @type {boolean} */
    this._isReady = false;
    /** @type {Promise<IndexedDBStorage>} */
    this.ready = this.init();
  }

  /**
   * Checks whether a storage key is associated with Crazy Cribbage apps and settings.
   * @private
   * @param {string} key
   * @returns {boolean}
   */
  _isRelevantKey(key) {
    if (!key || typeof key !== 'string') return false;
    return (
      key.startsWith('crazy') ||
      key.startsWith('cc_') ||
      key.startsWith('cribbage') ||
      key.startsWith('pints') ||
      key.startsWith('skunk') ||
      key.startsWith('camera_wrap_w')
    );
  }

  /**
   * Synchronously reads existing localStorage keys into the cache.
   * @private
   */
  _prepopulateFromLocalStorage() {
    if (!this.migrateLocalStorage || typeof window === 'undefined' || typeof window.localStorage === 'undefined') return;
    try {
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (this._isRelevantKey(key)) {
          this._cache.set(key, String(window.localStorage.getItem(key)));
        }
      }
    } catch (e) {
      console.warn('[IndexedDBStorage] LocalStorage pre-read warning:', e);
    }
  }

  /**
   * Initializes the IndexedDB database connection, sets up object stores,
   * loads existing records into the in-memory cache, and migrates legacy localStorage.
   * @returns {Promise<IndexedDBStorage>}
   */
  async init() {
    if (typeof window === 'undefined') {
      this._isReady = true;
      return this;
    }

    // Refresh pre-populated cache from localStorage in case any items were stored immediately before init
    this._prepopulateFromLocalStorage();

    const idb = typeof indexedDB !== 'undefined' ? indexedDB : (window.indexedDB || null);
    if (!idb) {
      console.warn('[IndexedDBStorage] IndexedDB not supported; using in-memory cache.');
      this._isReady = true;
      return this;
    }

    return new Promise((resolve) => {
      try {
        const req = idb.open(this.dbName, this.version);

        req.onupgradeneeded = (e) => {
          const db = e.target.result;
          if (!db.objectStoreNames.contains(this.storeName)) {
            db.createObjectStore(this.storeName);
          }
        };

        req.onsuccess = async (e) => {
          this._db = e.target.result;
          try {
            await this._warmCacheFromDb();
            if (this.migrateLocalStorage) {
              await this._performLocalStorageMigration();
            }
          } catch (err) {
            console.warn('[IndexedDBStorage] Cache warm/migration warning:', err);
          }
          this._isReady = true;
          resolve(this);
        };

        req.onerror = (e) => {
          console.warn('[IndexedDBStorage] Failed to open IndexedDB:', e.target.error);
          this._isReady = true;
          resolve(this);
        };
      } catch (err) {
        console.warn('[IndexedDBStorage] Unexpected init exception:', err);
        this._isReady = true;
        resolve(this);
      }
    });
  }

  /**
   * Reads all key-value entries from IndexedDB into the synchronous cache.
   * @private
   * @returns {Promise<void>}
   */
  _warmCacheFromDb() {
    return new Promise((resolve, reject) => {
      if (!this._db) return resolve();
      try {
        const tx = this._db.transaction(this.storeName, 'readonly');
        const store = tx.objectStore(this.storeName);

        if (typeof store.openCursor === 'function') {
          const cursorReq = store.openCursor();
          cursorReq.onsuccess = (e) => {
            const cursor = e.target.result;
            if (cursor) {
              const k = String(cursor.key);
              const v = String(cursor.value);
              this._cache.set(k, v);
              if (typeof window !== 'undefined' && window.localStorage) {
                try {
                  window.localStorage.setItem(k, v);
                } catch (_) {}
              }
              cursor.continue();
            } else {
              resolve();
            }
          };
          cursorReq.onerror = (e) => reject(e.target.error);
        } else if (typeof store.getAllKeys === 'function') {
          const keysReq = store.getAllKeys();
          keysReq.onsuccess = () => {
            const keys = keysReq.result || [];
            const valsReq = store.getAll();
            valsReq.onsuccess = () => {
              const vals = valsReq.result || [];
              for (let i = 0; i < keys.length; i++) {
                const k = String(keys[i]);
                const v = String(vals[i]);
                this._cache.set(k, v);
                if (typeof window !== 'undefined' && window.localStorage) {
                  try {
                    window.localStorage.setItem(k, v);
                  } catch (_) {}
                }
              }
              resolve();
            };
            valsReq.onerror = (e) => reject(e.target.error);
          };
          keysReq.onerror = (e) => reject(e.target.error);
        } else {
          resolve();
        }
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * Copies relevant legacy localStorage items into IndexedDB and in-memory cache.
   * @private
   * @returns {Promise<void>}
   */
  async _performLocalStorageMigration() {
    if (typeof window === 'undefined' || typeof window.localStorage === 'undefined') return;

    try {
      const keysToMigrate = [];
      for (let i = 0; i < window.localStorage.length; i++) {
        const key = window.localStorage.key(i);
        if (this._isRelevantKey(key)) {
          keysToMigrate.push(key);
        }
      }

      for (const k of keysToMigrate) {
        const val = window.localStorage.getItem(k);
        if (val !== null && !this._cache.has(k)) {
          this._cache.set(k, String(val));
          await this._writeToDb(k, String(val));
        }
      }
    } catch (e) {
      console.warn('[IndexedDBStorage] LocalStorage migration error:', e);
    }
  }

  /**
   * Persists a key-value record to IndexedDB.
   * @private
   * @param {string} key
   * @param {string} val
   * @returns {Promise<void>}
   */
  _writeToDb(key, val) {
    return new Promise((resolve) => {
      if (!this._db) return resolve();
      try {
        const tx = this._db.transaction(this.storeName, 'readwrite');
        const store = tx.objectStore(this.storeName);
        const req = store.put(String(val), String(key));
        req.onsuccess = () => resolve();
        req.onerror = () => resolve(); // Non-blocking
      } catch (err) {
        resolve();
      }
    });
  }

  /**
   * Deletes a key from IndexedDB.
   * @private
   * @param {string} key
   * @returns {Promise<void>}
   */
  _deleteFromDb(key) {
    return new Promise((resolve) => {
      if (!this._db) return resolve();
      try {
        const tx = this._db.transaction(this.storeName, 'readwrite');
        const store = tx.objectStore(this.storeName);
        const req = store.delete(String(key));
        req.onsuccess = () => resolve();
        req.onerror = () => resolve();
      } catch (err) {
        resolve();
      }
    });
  }

  /**
   * Clears all records from IndexedDB.
   * @private
   * @returns {Promise<void>}
   */
  _clearDb() {
    return new Promise((resolve) => {
      if (!this._db) return resolve();
      try {
        const tx = this._db.transaction(this.storeName, 'readwrite');
        const store = tx.objectStore(this.storeName);
        const req = store.clear();
        req.onsuccess = () => resolve();
        req.onerror = () => resolve();
      } catch (err) {
        resolve();
      }
    });
  }

  // ==========================================
  // Synchronous Web Storage API Compatibility
  // ==========================================

  /**
   * Returns current value associated with the given key, or null if key does not exist.
   * @param {string} key
   * @returns {string|null}
   */
  getItem(key) {
    const k = String(key);
    if (this._cache.has(k)) {
      return this._cache.get(k);
    }
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        const val = window.localStorage.getItem(k);
        if (val !== null) {
          const sVal = String(val);
          this._cache.set(k, sVal);
          return sVal;
        }
      } catch (e) {}
    }
    return null;
  }

  /**
   * Sets the value of the pair identified by key to value, creating a new key/value pair if none existed.
   * Synchronously updates in-memory cache, mirrors to localStorage, and asynchronously writes to IndexedDB.
   * @param {string} key
   * @param {string|number|boolean} value
   */
  setItem(key, value) {
    const k = String(key);
    const v = String(value);
    this._cache.set(k, v);
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.setItem(k, v);
      } catch (e) {}
    }
    this._writeToDb(k, v).catch(() => {});
  }

  /**
   * Removes the key/value pair with the given key.
   * Synchronously removes from in-memory cache and localStorage, and asynchronously deletes from IndexedDB.
   * @param {string} key
   */
  removeItem(key) {
    const k = String(key);
    this._cache.delete(k);
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.removeItem(k);
      } catch (e) {}
    }
    this._deleteFromDb(k).catch(() => {});
  }

  /**
   * Removes all key/value pairs.
   * Synchronously clears in-memory cache and localStorage, and asynchronously clears IndexedDB.
   */
  clear() {
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        for (const k of Array.from(this._cache.keys())) {
          window.localStorage.removeItem(k);
        }
      } catch (e) {}
    }
    this._cache.clear();
    this._clearDb().catch(() => {});
  }

  /**
   * Returns the name of the nth key in the list, or null if n is greater than or equal to the number of key/value pairs.
   * @param {number} index
   * @returns {string|null}
   */
  key(index) {
    const keys = Array.from(this._cache.keys());
    return index >= 0 && index < keys.length ? keys[index] : null;
  }

  /**
   * Number of data items stored.
   * @returns {number}
   */
  get length() {
    return this._cache.size;
  }

  // ==========================================
  // Asynchronous Promise-Based API
  // ==========================================

  /**
   * Asynchronously retrieves a value, awaiting DB readiness if still initializing.
   * @param {string} key
   * @returns {Promise<string|null>}
   */
  async getAsync(key) {
    if (!this._isReady) await this.ready;
    return this.getItem(key);
  }

  /**
   * Asynchronously sets a key-value pair and waits for IndexedDB commit.
   * @param {string} key
   * @param {string|number|boolean} value
   * @returns {Promise<void>}
   */
  async setAsync(key, value) {
    if (!this._isReady) await this.ready;
    const k = String(key);
    const v = String(value);
    this._cache.set(k, v);
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.setItem(k, v);
      } catch (e) {}
    }
    await this._writeToDb(k, v);
  }

  /**
   * Asynchronously deletes a key and waits for IndexedDB commit.
   * @param {string} key
   * @returns {Promise<void>}
   */
  async deleteAsync(key) {
    if (!this._isReady) await this.ready;
    const k = String(key);
    this._cache.delete(k);
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        window.localStorage.removeItem(k);
      } catch (e) {}
    }
    await this._deleteFromDb(k);
  }

  /**
   * Asynchronously clears all stored items and waits for IndexedDB commit.
   * @returns {Promise<void>}
   */
  async clearAsync() {
    if (!this._isReady) await this.ready;
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        for (const k of Array.from(this._cache.keys())) {
          window.localStorage.removeItem(k);
        }
      } catch (e) {}
    }
    this._cache.clear();
    await this._clearDb();
  }

  /**
   * Returns an array of all keys currently stored.
   * @returns {Promise<Array<string>>}
   */
  async keysAsync() {
    if (!this._isReady) await this.ready;
    return Array.from(this._cache.keys());
  }

  /**
   * Returns a dictionary object containing all key-value entries.
   * @returns {Promise<Object<string, string>>}
   */
  async getAllAsync() {
    if (!this._isReady) await this.ready;
    const res = {};
    for (const [k, v] of this._cache.entries()) {
      res[k] = v;
    }
    return res;
  }
}

/** Global default singleton instance */
export const appStorage = new IndexedDBStorage();

if (typeof window !== 'undefined') {
  window.IndexedDBStorage = IndexedDBStorage;
  window.appStorage = appStorage;
}
